/**
 * All Products Section
 * A first-class premium-theme section for rendering the full storefront
 * catalog wherever the merchant places it in the theme builder.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import HomeProductCatalog from '../storefront/HomeProductCatalog';
import { fetchCart } from '../../features/cart/cartSlice';
import { useCartTransition } from '../../hooks/useViewTransition';
import { useStorefrontCurrency } from '../../contexts/StorefrontCurrencyContext';
import { useGA4 } from '../../contexts/GA4Context';
import { useToast } from '../ui/Toast';

const DEFAULT_PRODUCTS_PER_PAGE = 12;

const isOnSubdomain = () => {
  const hostname = window.location.hostname;
  return hostname.includes('.') && !hostname.startsWith('www.');
};

function AllProducts({ settings = {}, content = {}, globalStyles = {}, store }) {
  const dispatch = useDispatch();
  const products = useSelector(state => state.store?.products || []);
  const { transitionCartAction, isUpdating } = useCartTransition();
  const { formatPrice } = useStorefrontCurrency();
  const { trackSearch } = useGA4();
  const toast = useToast();
  const sectionRef = useRef(null);

  const productsPerPage = Number(settings.productsPerPage) || DEFAULT_PRODUCTS_PER_PAGE;
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [viewMode, setViewMode] = useState(settings.defaultView || 'grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [wishlist, setWishlist] = useState([]);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  const colors = globalStyles.colors || {};
  const themeStyles = {
    primary: settings.accentColor || colors.primary || '#0d7463',
    background: settings.backgroundColor || colors.background || '#ffffff',
    backgroundAlt: settings.surfaceColor || colors.surface || '#f9fafb',
    text: settings.textColor || colors.text || '#111827',
    textMuted: settings.mutedColor || colors.muted || '#6b7280',
    border: settings.borderColor || colors.border || '#e5e7eb',
  };
  const catalogTheme = { fonts: globalStyles.fonts || {} };

  const getStorePath = (path = '') => {
    if (isOnSubdomain()) {
      return path ? `/${path}` : '/';
    }
    return path ? `/s/${store?.slug}/${path}` : `/s/${store?.slug}`;
  };

  useEffect(() => {
    if (store?.slug) {
      dispatch(fetchCart(null));
    }
  }, [dispatch, store?.slug]);

  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        const response = await fetch('/api/customer/wishlist', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setWishlist(data.wishlist || []);
        }
      } catch {
        // Wishlist is optional for anonymous storefront visitors.
      }
    };

    fetchWishlist();
  }, []);

  const activeProducts = useMemo(() => (
    Array.isArray(products)
      ? products.filter(product => !product.status || product.status === 'ACTIVE')
      : []
  ), [products]);

  const categories = useMemo(() => (
    [...new Set(activeProducts.map(product => product.category).filter(Boolean))]
  ), [activeProducts]);

  const filteredProducts = useMemo(() => {
    let filtered = [...activeProducts];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query)
      );
    }

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    if (sortBy === 'a-z') {
      filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'z-a') {
      filtered.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
    } else if (sortBy === 'price-low') {
      filtered.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
    }

    return filtered;
  }, [activeProducts, searchQuery, selectedCategory, sortBy]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);
  const startIndex = (currentPage - 1) * productsPerPage;
  const paginatedProducts = filteredProducts.slice(startIndex, startIndex + productsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, sortBy, productsPerPage]);

  useEffect(() => {
    if (searchQuery.trim().length >= 3) {
      const timer = setTimeout(() => trackSearch(searchQuery.trim()), 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [searchQuery, trackSearch]);

  const toggleWishlist = async (productId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setWishlistLoading(true);

    try {
      const response = await fetch('/api/customer/wishlist/toggle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ productId }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.inWishlist) {
          setWishlist(prev => [...prev, { id: productId }]);
          toast.success('Added to wishlist');
        } else {
          setWishlist(prev => prev.filter(item => item.id !== productId));
          toast.info('Removed from wishlist');
        }
      } else if (response.status === 401) {
        sessionStorage.setItem('pendingWishlistProduct', productId);
        toast.warning('Log in to save this product');
      } else {
        toast.error('Failed to update wishlist');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setWishlistLoading(false);
    }
  };

  const isInWishlist = (productId) => wishlist.some(item => item.id === productId);

  return (
    <section className="ts-all-products" style={{ backgroundColor: themeStyles.background }}>
      <HomeProductCatalog
        sectionRef={sectionRef}
        selectedCategory={selectedCategory}
        filteredProducts={filteredProducts}
        paginatedProducts={paginatedProducts}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        categories={categories}
        onCategoryChange={(category) => {
          setSelectedCategory(category);
          setCurrentPage(1);
        }}
        sortBy={sortBy}
        onSortChange={setSortBy}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        themeStyles={themeStyles}
        theme={catalogTheme}
        store={store}
        formatPrice={formatPrice}
        getStorePath={getStorePath}
        dispatch={dispatch}
        transitionCartAction={transitionCartAction}
        isUpdating={isUpdating}
        toggleWishlist={toggleWishlist}
        isInWishlist={isInWishlist}
        wishlistLoading={wishlistLoading}
        sectionTitle={content.title || 'All Products'}
        sectionSubtitle={content.subtitle}
        showControls={settings.showControls !== false}
      />
    </section>
  );
}

export default AllProducts;
